import { environment } from '../../../environments/environment';
import {
  EVENT_STATUS_LANDING_BY_VALUE,
  EVENT_TAB_ID,
  EventLandingStrategy,
  EventTabId,
} from './event-landing-tab.constants';
import { LEAGUE_EVENT_TYPE_CODES } from '../event-type/event-type.constants';

export interface EventLandingTabOptions {
  tenantName?: string;
  /** Normalised event type (e.g. 'T', 'L', 'BT', 'BL'). Used for tenant overrides. */
  eventType?: string;
  isOverviewAvailable?: boolean;
  /** Preserves legacy "see all videos" navigation to Live Matches. */
  forceLiveMatchesTab?: boolean;
  /** When provided, returns a tab id that exists in the filtered sidebar list. */
  availableTabIds?: readonly number[];
}

export type EventStatusInput = number | string | null | undefined;

/**
 * Normalizes API/card event_status (number or numeric string) to a finite number.
 */
export function normalizeEventStatus(
  eventStatus: EventStatusInput,
): number | null {
  if (eventStatus === null || eventStatus === undefined || eventStatus === '') {
    return null;
  }
  const parsed = Number(eventStatus);
  return Number.isFinite(parsed) ? parsed : null;
}

export function getEventLandingStrategy(
  eventStatus: EventStatusInput,
): EventLandingStrategy {
  const status = normalizeEventStatus(eventStatus);
  if (
    status !== null &&
    EVENT_STATUS_LANDING_BY_VALUE.get(status) === 'draws_results'
  ) {
    return EventLandingStrategy.DRAWS_RESULTS;
  }
  return EventLandingStrategy.FIRST_TAB;
}

/**
 * Tenant-specific first tab: ETTU → Schedule; other tenants → Overview when enabled.
 */
export function getFirstTabIdForTenant(
  tenantName: string = environment.tenant_Name,
  isOverviewAvailable: boolean = environment.is_overview_available,
): EventTabId {
  if (tenantName?.toLowerCase() === 'ettu') {
    return EVENT_TAB_ID.SCHEDULE;
  }
  if (isOverviewAvailable) {
    return EVENT_TAB_ID.OVERVIEW;
  }
  return EVENT_TAB_ID.SCHEDULE;
}

/**
 * Picks the nearest available tab when the preferred tab was filtered out (e.g. Overview on ETTU).
 */
export function resolveAvailableTabId(
  preferredTabId: number,
  availableTabIds: readonly number[],
): number {
  if (!availableTabIds.length) {
    return preferredTabId;
  }
  if (availableTabIds.includes(preferredTabId)) {
    return preferredTabId;
  }
  const fallbackOrder: EventTabId[] = [
    EVENT_TAB_ID.OVERVIEW,
    EVENT_TAB_ID.SCHEDULE,
    EVENT_TAB_ID.PARTICIPANTS,
    EVENT_TAB_ID.DRAWS_RESULTS,
    EVENT_TAB_ID.LIVE_MATCHES,
    EVENT_TAB_ID.AWARDS,
    EVENT_TAB_ID.LEADERBOARD,
  ];
  for (const tabId of fallbackOrder) {
    if (availableTabIds.includes(tabId)) {
      return tabId;
    }
  }
  return availableTabIds[0];
}

/**
 * Resolves the landing tab id for an event based on status and tenant configuration.
 */
export function resolveEventLandingTabId(
  eventStatus: number | string | null | undefined,
  options: EventLandingTabOptions = {},
): number {
  if (options.forceLiveMatchesTab) {
    return EVENT_TAB_ID.LIVE_MATCHES;
  }

  const tenant = options.tenantName ?? environment.tenant_Name;
  const eventType = String(options.eventType ?? '').trim().toUpperCase();

  // League landing tab override from env uiConfig.tabConfig (e.g. leagueLandingTab: 7 → Leaderboard).
  const tabCfg = (environment as any).uiConfig?.tabConfig;
  if (tabCfg?.leagueLandingTab != null && LEAGUE_EVENT_TYPE_CODES.has(eventType)) {
    const preferred = tabCfg.leagueLandingTab as number;
    if (options.availableTabIds?.length) {
      return resolveAvailableTabId(preferred, options.availableTabIds);
    }
    return preferred;
  }

  // No env override — status drives the landing tab for every event type
  // (Ongoing / Draws Out / Recent → Draws & Results, everything else → first tab).
  const strategy = getEventLandingStrategy(eventStatus);
  let tabId: number =
    strategy === EventLandingStrategy.DRAWS_RESULTS
      ? EVENT_TAB_ID.DRAWS_RESULTS
      : getFirstTabIdForTenant(tenant, options.isOverviewAvailable);

  if (options.availableTabIds?.length) {
    tabId = resolveAvailableTabId(tabId, options.availableTabIds);
  }

  return tabId;
}

/** Route segments for event detail navigation: [eventId, catId, participantId, tabId, childTab]. */
export function buildEventDetailRouteSegments(
  eventId: number | string,
  tabId: number,
  options?: { matchTab?: number; isSeeAllVideos?: boolean },
): (number | string)[] {
  const base: (number | string)[] = [eventId, 0, 0, tabId, tabId];
  if (tabId === EVENT_TAB_ID.LIVE_MATCHES) {
    const matchTab =
      options?.matchTab ??
      (options?.isSeeAllVideos ? 2 : 1);
    return [...base, matchTab];
  }
  return base;
}
